import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';

const BODY_ZONES = [
  { id: 'testa', label: 'Testa/Occipite', x: 150, y: 30, w: 40, h: 35 },
  { id: 'spalla_dx', label: 'Spalla Dx', x: 100, y: 80, w: 30, h: 25 },
  { id: 'spalla_sx', label: 'Spalla Sx', x: 210, y: 80, w: 30, h: 25 },
  { id: 'torace', label: 'Torace', x: 135, y: 95, w: 70, h: 50 },
  { id: 'gomito_dx', label: 'Gomito Dx', x: 80, y: 130, w: 25, h: 20 },
  { id: 'gomito_sx', label: 'Gomito Sx', x: 235, y: 130, w: 25, h: 20 },
  { id: 'sacro', label: 'Sacro', x: 145, y: 155, w: 50, h: 30 },
  { id: 'anca_dx', label: 'Anca/Trocantere Dx', x: 115, y: 170, w: 30, h: 25 },
  { id: 'anca_sx', label: 'Anca/Trocantere Sx', x: 195, y: 170, w: 30, h: 25 },
  { id: 'mano_dx', label: 'Mano Dx', x: 65, y: 185, w: 22, h: 22 },
  { id: 'mano_sx', label: 'Mano Sx', x: 253, y: 185, w: 22, h: 22 },
  { id: 'ginocchio_dx', label: 'Ginocchio Dx', x: 125, y: 240, w: 28, h: 22 },
  { id: 'ginocchio_sx', label: 'Ginocchio Sx', x: 187, y: 240, w: 28, h: 22 },
  { id: 'tibia_dx', label: 'Tibia Dx', x: 127, y: 275, w: 24, h: 30 },
  { id: 'tibia_sx', label: 'Tibia Sx', x: 189, y: 275, w: 24, h: 30 },
  { id: 'tallone_dx', label: 'Tallone Dx', x: 125, y: 320, w: 25, h: 20 },
  { id: 'tallone_sx', label: 'Tallone Sx', x: 190, y: 320, w: 25, h: 20 },
  { id: 'malleolo_dx', label: 'Malleolo Dx', x: 115, y: 310, w: 18, h: 15 },
  { id: 'malleolo_sx', label: 'Malleolo Sx', x: 207, y: 310, w: 18, h: 15 },
];

const STADIO_COLORS = {
  'I': '#fbbf24',
  'II': '#f97316',
  'III': '#ef4444',
  'IV': '#991b1b',
};

export default function BodyMapSelector({ lesioni = [], onChange }) {
  const [hoveredZone, setHoveredZone] = useState(null);

  const lesioniMap = {};
  lesioni.forEach((l, idx) => {
    const zone = BODY_ZONES.find(z => z.label === l.sede || z.id === l.sede.toLowerCase().replace(/[^a-z]/g, '_'));
    if (zone) {
      if (!lesioniMap[zone.id]) lesioniMap[zone.id] = [];
      lesioniMap[zone.id].push({ ...l, idx });
    }
  });

  const handleZoneClick = (zone) => {
    if (!onChange) return;
    const exists = lesioni.findIndex(l => l.sede === zone.label);
    if (exists >= 0) return;
    onChange([...lesioni, { sede: zone.label, lato: '', tipo: 'pressione', stadio: 'I', note: '' }]);
  };

  return (
    <div className="flex gap-4 items-start" data-testid="body-map">
      {/* SVG Body Map */}
      <div className="relative bg-slate-50 rounded-xl border p-2 flex-shrink-0" style={{ width: 340, height: 370 }}>
        <svg viewBox="0 0 340 360" className="w-full h-full">
          {/* Body outline */}
          <g stroke="#94a3b8" strokeWidth="1.5" fill="none">
            {/* Head */}
            <ellipse cx="170" cy="42" rx="22" ry="28" fill="#e2e8f0" />
            {/* Neck */}
            <rect x="162" y="68" width="16" height="12" rx="3" fill="#e2e8f0" />
            {/* Torso */}
            <path d="M130 80 Q125 80 120 90 L115 150 Q115 185 135 195 L140 195 Q145 200 170 200 Q195 200 200 195 L205 195 Q225 185 225 150 L220 90 Q215 80 210 80 Z" fill="#e2e8f0" />
            {/* Left arm */}
            <path d="M120 90 Q105 95 95 120 L85 160 Q80 175 75 190 L70 200 Q68 208 78 208 L88 200 Q95 185 100 165 L110 130 Q115 115 120 105" fill="#e2e8f0" />
            {/* Right arm */}
            <path d="M220 90 Q235 95 245 120 L255 160 Q260 175 265 190 L270 200 Q272 208 262 208 L252 200 Q245 185 240 165 L230 130 Q225 115 220 105" fill="#e2e8f0" />
            {/* Left leg */}
            <path d="M140 195 Q135 210 133 240 L130 280 Q128 310 130 330 Q130 345 140 345 L145 340 Q148 330 147 310 L148 275 Q148 250 150 230 Q152 215 155 200" fill="#e2e8f0" />
            {/* Right leg */}
            <path d="M200 195 Q205 210 207 240 L210 280 Q212 310 210 330 Q210 345 200 345 L195 340 Q192 330 193 310 L192 275 Q192 250 190 230 Q188 215 185 200" fill="#e2e8f0" />
          </g>

          {/* Interactive zones */}
          {BODY_ZONES.map(zone => {
            const hasLesion = lesioniMap[zone.id];
            const isHovered = hoveredZone === zone.id;
            const color = hasLesion ? STADIO_COLORS[hasLesion[0].stadio] || '#ef4444' : (isHovered && onChange ? '#93c5fd' : 'transparent');

            return (
              <g key={zone.id}>
                <rect
                  x={zone.x} y={zone.y} width={zone.w} height={zone.h}
                  rx={4} fill={color} fillOpacity={hasLesion ? 0.5 : 0.3}
                  stroke={hasLesion ? (STADIO_COLORS[hasLesion[0].stadio] || '#ef4444') : (isHovered && onChange ? '#3b82f6' : 'transparent')}
                  strokeWidth={hasLesion ? 2 : 1}
                  className={onChange ? 'cursor-pointer' : ''}
                  onMouseEnter={() => setHoveredZone(zone.id)}
                  onMouseLeave={() => setHoveredZone(null)}
                  onClick={() => handleZoneClick(zone)}
                  data-testid={`body-zone-${zone.id}`}
                />
                {hasLesion && (
                  <text x={zone.x + zone.w / 2} y={zone.y + zone.h / 2 + 4} textAnchor="middle" fontSize="11" fontWeight="bold" fill="#fff" pointerEvents="none">
                    {hasLesion[0].stadio}
                  </text>
                )}
              </g>
            );
          })}

          {/* Hover label */}
          {hoveredZone && (
            <g>
              <rect x="85" y="350" width="170" height="18" rx="4" fill="#1e293b" fillOpacity="0.9" />
              <text x="170" y="363" textAnchor="middle" fontSize="10" fill="#fff">
                {BODY_ZONES.find(z => z.id === hoveredZone)?.label || ''}
                {onChange ? ' (clicca per aggiungere)' : ''}
              </text>
            </g>
          )}
        </svg>
      </div>

      {/* Legend */}
      <div className="space-y-2">
        <p className="text-xs font-semibold text-slate-500">Legenda Stadi</p>
        {Object.entries(STADIO_COLORS).map(([stadio, color]) => (
          <div key={stadio} className="flex items-center gap-2">
            <div className="w-4 h-4 rounded" style={{ backgroundColor: color, opacity: 0.6 }} />
            <span className="text-[10px] text-slate-600">Stadio {stadio}</span>
          </div>
        ))}
        {lesioni.length > 0 && (
          <>
            <Separator className="my-2" />
            <p className="text-xs font-semibold text-slate-500">Lesioni ({lesioni.length})</p>
            {lesioni.map((l, i) => (
              <Badge key={i} variant="outline" className="text-[10px] block">
                {l.sede} - St.{l.stadio} ({l.tipo})
              </Badge>
            ))}
          </>
        )}
        {onChange && lesioni.length === 0 && (
          <p className="text-[10px] text-slate-400 mt-2">Clicca sulle zone del corpo per aggiungere una lesione</p>
        )}
      </div>
    </div>
  );
}
